import React, { useState, useEffect, useRef } from 'react'
import {
  useNavigate,
  useSearchParams,
  Link as RouterLink,
} from 'react-router-dom'

import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Link,
  Tooltip,
  useTheme,
  useMediaQuery,
  keyframes,
} from '@mui/material'
import { styled, createTheme, ThemeProvider } from '@mui/material/styles'
import {
  InfoOutlined,
  LinkedIn,
  Shield,
} from '@mui/icons-material'
import { useAuth } from '../context/AuthContext'
import clarityService from '../services/ClarityService'

// Enhanced theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#ffda1b',
      contrastText: '#232536',
    },
    secondary: {
      main: '#232536',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f9fafb',
      paper: '#ffffff',
    },
    text: {
      primary: '#232536',
      secondary: '#4b5563',
    },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
  },
})

// Keyframe animations from Register component
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`

const slideInRight = keyframes`
  0% {
    transform: translateX(100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideInLeft = keyframes`
  0% {
    transform: translateX(-50px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
`

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`

// Modern Google logo component
const GoogleLogo = () => (
  <Box
    component='svg'
    sx={{ width: 20, height: 20, mr: 1 }}
    viewBox='0 0 24 24'
  >
    <path
      fill='#4285F4'
      d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
    />
    <path
      fill='#34A853'
      d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
    />
    <path
      fill='#FBBC05'
      d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
    />
    <path
      fill='#EA4335'
      d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
    />
  </Box>
)

// Main layout container
const LayoutContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#f9fafb',
}))

// Animated background shapes
const AnimatedShape = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  filter: 'blur(40px)',
  animation: `${float} 8s infinite ease-in-out`,
  '&.shape-1': {
    top: '10%',
    left: '5%',
    width: '200px',
    height: '200px',
    backgroundColor: 'rgba(255, 218, 27, 0.1)',
  },
  '&.shape-2': {
    bottom: '10%',
    right: '5%',
    width: '240px',
    height: '240px',
    backgroundColor: 'rgba(255, 218, 27, 0.08)',
    animationDelay: '4s',
  },
}))

// Background gradient overlay
const BackgroundGradient = styled(Box)({
  position: 'absolute',
  inset: 0,
  background:
    'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 70%, rgba(243, 244, 246, 0.1) 100%)',
  opacity: 0.7,
})

// Hero section with animated background
const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #232536 0%, #374151 100%)',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '40vh',
  padding: theme.spacing(4, 2),
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    minHeight: '35vh',
    padding: theme.spacing(3, 2),
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '30vh',
    padding: theme.spacing(2, 1),
  },
}))

// Content section with proper spacing
const ContentSection = styled(Container)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
    alignItems: 'flex-start',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
}))

// Animated icon container
const IconContainer = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  backgroundColor: 'rgba(255, 218, 27, 0.15)',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  animation: `${pulse} 3s ease-in-out infinite`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
}))

// Yellow accent line
const AccentLine = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '3px',
  backgroundColor: '#ffda1b',
  margin: '0 auto',
  borderRadius: '2px',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    width: '48px',
    marginBottom: theme.spacing(2),
  },
}))

// Auth card with smooth slide-in animation
const AuthCard = styled(Paper)(({ theme }) => ({
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: theme.spacing(3.5),
  boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.12)',
  border: '1px solid #e2e8f0',
  width: '100%',
  maxWidth: '420px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: 'fit-content',
  animation: `${fadeInUp} 0.8s ease-out`,
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.18)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
    borderRadius: '12px',
    margin: theme.spacing(0, 1),
  },
}))

// Video container with slide-in animation
const VideoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  animation: `${slideInRight} 0.8s ease-out`,
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}))

// Phone frame with hover animations
const PhoneFrame = styled(Box)(({ theme }) => ({
  position: 'relative',
  backgroundColor: '#374151',
  padding: '8px',
  borderRadius: '24px',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.4s ease',
  animation: `${float} 6s ease-in-out infinite`,
  '&:hover': {
    transform: 'translateY(-12px) scale(1.02)',
    boxShadow: '0 30px 60px rgba(0, 0, 0, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '20px',
    padding: '6px',
  },
}))

const PhoneScreen = styled(Box)(({ theme }) => ({
  backgroundColor: '#000',
  borderRadius: '16px',
  overflow: 'hidden',
  position: 'relative',
  [theme.breakpoints.down('sm')]: {
    borderRadius: '14px',
  },
}))

// Social buttons with enhanced animations
const SocialButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'white',
  color: '#374151',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: theme.spacing(1.75, 2.5),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '1rem',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: '#f8fafc',
    borderColor: '#ffda1b',
    transform: 'translateY(-3px)',
    boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.95rem',
  },
}))

// Set to 'portrait' or 'landscape' to match the video orientation.
const VIDEO_ORIENTATION = 'landscape'

// Video component with responsive sizing
const VideoComponent = () => {
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'))
  const isBelowLargeDesktop = useMediaQuery('(max-width:1199.95px)')

  const isLandscape = VIDEO_ORIENTATION === 'landscape'

  // Portrait sizes  (width < height, 9:16 ratio)
  // Landscape sizes (width > height, 16:9 ratio)
  const getVideoSize = () => {
    if (isMobile) {
      return isLandscape
        ? { width: '320px', height: '180px' }
        : { width: '220px', height: '391px' }
    }
    if (isBelowLargeDesktop) {
      return isLandscape
        ? { width: '426px', height: '240px' }
        : { width: '260px', height: '462px' }
    }
    return isLandscape
      ? { width: '512px', height: '288px' }
      : { width: '300px', height: '533px' }
  }

  const videoSize = getVideoSize()

  // For landscape, the phone "notch" sits on the left side
  const notchSx = isLandscape
    ? {
        position: 'absolute',
        top: '50%',
        left: '6px',
        transform: 'translateY(-50%)',
        width: '3px',
        height: '32px',
        backgroundColor: '#6b7280',
        borderRadius: '2px',
      }
    : {
        position: 'absolute',
        top: '6px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '32px',
        height: '3px',
        backgroundColor: '#6b7280',
        borderRadius: '2px',
      }

  // On desktop + landscape, the phone frame stretches to match the card height.
  const isDesktop = !isBelowLargeDesktop
  const phoneFrameSx =
    isLandscape && isDesktop ? { alignSelf: 'stretch', height: '100%' } : {}
  const phoneScreenSx =
    isLandscape && isDesktop
      ? { height: '100%', width: 'auto', aspectRatio: '16 / 9' }
      : videoSize

  return (
    <VideoContainer>
      <PhoneFrame sx={phoneFrameSx}>
        <PhoneScreen sx={phoneScreenSx}>
          <Box
            component='iframe'
            src='https://www.youtube.com/embed/0rWd-5lYHLk?showinfo=0&rel=0&iv_load_policy=3&modestbranding=1'
            title='GCC Tax Laws Introduction'
            sx={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          />
        </PhoneScreen>

        {/* Phone notch adapts to orientation */}
        <Box sx={notchSx} />
      </PhoneFrame>
    </VideoContainer>
  )
}

const sanitizeRedirectPath = (path) => {
  if (!path || typeof path !== 'string') {
    return null
  }

  const normalizedPath = path.trim()
  if (!normalizedPath.startsWith('/') || normalizedPath.startsWith('//')) {
    return null
  }

  if (
    normalizedPath === '/login' ||
    normalizedPath.startsWith('/login?') ||
    normalizedPath === '/oauth2/callback/success' ||
    normalizedPath.startsWith('/oauth2/callback/success?')
  ) {
    return null
  }

  return normalizedPath
}

const consumeRedirectPath = () => {
  const savedPath = sanitizeRedirectPath(sessionStorage.getItem('redirectPath'))
  sessionStorage.removeItem('redirectPath')
  return savedPath
}

const redirectAfterSignIn = (navigate) => {
  const redirectPath = consumeRedirectPath()
  navigate(redirectPath || '/', { replace: true })
}

const getCurrentInternalPath = () => {
  const path = `${window.location.pathname}${window.location.search}${window.location.hash || ''}`
  return sanitizeRedirectPath(path)
}

const SignIn = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, loginSuccess, checkSession } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [sessionNotice, setSessionNotice] = useState('')
  const popupRef = useRef(null)
  const checkIntervalRef = useRef(null)
  const authResultHandledRef = useRef(false)

  // Responsive breakpoints

  // Listen for messages from popup window
  useEffect(() => {
    const stopPopupWatch = () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close()
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current)
        checkIntervalRef.current = null
      }
    }

    const clearOAuthSessionState = () => {
      sessionStorage.removeItem('auth_flow_started')
      sessionStorage.removeItem('auth_provider')
      sessionStorage.removeItem('auth_start_time')
      sessionStorage.removeItem('oauth_original_domain')
      sessionStorage.removeItem('oauth_callback_domain')
    }

    const handleAuthSuccess = (payload, source = 'post_message') => {
      if (authResultHandledRef.current) return
      if (!payload?.user) return

      authResultHandledRef.current = true
      stopPopupWatch()

      clarityService.trackAuthSuccess(payload.user)
      clarityService.trackEvent('oauth2_popup_success', {
        provider: payload.provider,
        source,
        timestamp: new Date().toISOString(),
        domain: window.location.hostname,
      })

      clearOAuthSessionState()
      loginSuccess(payload.user)
      setLoading(false)
      redirectAfterSignIn(navigate)
    }

    const handleAuthError = (payload, source = 'post_message') => {
      if (authResultHandledRef.current) return

      authResultHandledRef.current = true
      stopPopupWatch()
      setLoading(false)

      const errorMessage =
        payload?.error || 'Authentication failed. Please try again.'
      setError(errorMessage)
      clarityService.trackAuthFailure({
        message: errorMessage,
        source,
        timestamp: new Date().toISOString(),
      })
    }

    const handleMessage = (event) => {
      if (event.origin !== window.location.origin) {
        console.warn(
          'Received message from invalid origin:',
          event.origin
        )
        return
      }

      if (event.data && event.data.type === 'OAUTH2_SUCCESS') {
        handleAuthSuccess(event.data, 'post_message')
      } else if (event.data && event.data.type === 'OAUTH2_ERROR') {
        handleAuthError(event.data, 'post_message')
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
      stopPopupWatch()
    }
  }, [loginSuccess, navigate])

  useEffect(() => {
    const authNotice = sessionStorage.getItem('auth_notice')
    if (authNotice === 'session_expired') {
      setSessionNotice('Session expired due to multiple logins from same e-mail id.')
      sessionStorage.removeItem('auth_notice')
    }
  }, [])

  useEffect(() => {
    // Track login page visit
    clarityService.trackPageView(window.location.pathname, {
      pageType: 'authentication',
      isLoginPage: true,
      referrer: document.referrer,
      hasAuthFlow: !!sessionStorage.getItem('auth_flow_started'),
    })

    // Check for auth errors in URL and track them
    const errorParam = searchParams.get('error')
    if (errorParam) {
      clarityService.trackEvent('auth_error_received', {
        error: errorParam,
        message: searchParams.get('message'),
        timestamp: new Date().toISOString(),
        source: 'signin_page_load',
      })
    }
    const authFlowStarted = sessionStorage.getItem('auth_flow_started')
    if (authFlowStarted) {
      const authStartTime = sessionStorage.getItem('auth_start_time')
      const timeSinceStart = authStartTime
        ? Date.now() - parseInt(authStartTime)
        : 0

      clarityService.trackEvent('auth_flow_return_to_signin', {
        timeSinceStart,
        hasError: !!errorParam,
        timestamp: new Date().toISOString(),
      })
    }
  }, [searchParams])

  useEffect(() => {
    // If already authenticated, redirect
    if (isAuthenticated) {
      sessionStorage.removeItem('auth_flow_started')
      sessionStorage.removeItem('auth_provider')
      sessionStorage.removeItem('auth_start_time')
      sessionStorage.removeItem('oauth_original_domain')
      sessionStorage.removeItem('oauth_callback_domain')

      clarityService.trackEvent('signin_already_authenticated', {
        timestamp: new Date().toISOString(),
      })
      redirectAfterSignIn(navigate)
    }

    // Check for OAuth2 errors
    const errorParam = searchParams.get('error')
    if (errorParam) {
      setError(getErrorMessage(errorParam))
    }
  }, [isAuthenticated, navigate, searchParams])

  const getErrorMessage = (errorCode) => {
    const errorMessages = {
      oauth2_error: 'Authentication failed. Please try again.',
      oauth2_failed: 'OAuth2 authentication failed.',
      access_denied:
        'Access was denied. Please grant the required permissions.',
      account_inactive: 'Your account is inactive. Please contact support@gcctaxlaws.com.',
    }
    return errorMessages[errorCode] || 'Authentication failed.'
  }

const handleOAuth2Login = (provider) => {
  setLoading(true)
  setError('')
  authResultHandledRef.current = false

  try {
    // Store the current domain before starting OAuth flow
    const currentDomain = window.location.host // Includes port number
    // Validate domain
    const validDomains = [
      'gcctaxlaws.com',
      'uaetaxlaws.com',
      'ksataxlaws.com',
      'kuwaittaxlaws.com',
      'qatartaxlaws.com',
      'omantaxlaws.com',
      'bahraintaxlaws.com',
      'cra.uaetaxlaws.com',
      'payments.uaetaxlaws.com',
    ]

    const isLocalDevelopment = (domain) => {
      const domainWithoutPort = domain.split(':')[0]
      return (
        domainWithoutPort === 'localhost' ||
        domainWithoutPort === '127.0.0.1' ||
        domainWithoutPort.match(/^192\.168\.\d+\.\d+$/) ||
        domainWithoutPort.match(/^10\.\d+\.\d+\.\d+$/) ||
        domainWithoutPort.match(/^172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+$/)
      )
    }

    const domainWithoutPort = currentDomain.split(':')[0]
    const isValidDomain =
      validDomains.includes(domainWithoutPort) ||
      validDomains.includes(domainWithoutPort.replace('www.', '')) ||
      isLocalDevelopment(currentDomain)

    if (!isValidDomain) {
      throw new Error(`Invalid domain: ${currentDomain}`)
    }

    // Track auth start
    clarityService.trackAuthStart(provider)

    // Store current session info
    sessionStorage.setItem('auth_flow_started', 'true')
    sessionStorage.setItem('auth_provider', provider)
    sessionStorage.setItem('auth_start_time', Date.now().toString())

    // Store the intended redirect path if not on login page
    const currentPath = getCurrentInternalPath()
    if (currentPath) {
      sessionStorage.setItem('redirectPath', currentPath)
    }

    clarityService.trackEvent('oauth2_popup_attempt', {
      provider,
      domain: currentDomain,
      timestamp: new Date().toISOString(),
      currentPath: window.location.pathname,
      userAgent: navigator.userAgent.substring(0, 100),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      hasExistingSession: !!localStorage.getItem('clarity_user_id'),
    })

    // Determine backend URL
    let backendBaseUrl
    if (isLocalDevelopment(currentDomain)) {
      backendBaseUrl = 'http://localhost:8080'
    } else {
      backendBaseUrl = `${window.location.protocol}//${currentDomain}`
    }
    // OAuth endpoint URL
    const oauthUrl = `${backendBaseUrl}/api/oauth2/authorization/${provider}`

    // Open popup window
    const popupWidth = 500
    const popupHeight = 600
    const left = window.screen.width / 2 - popupWidth / 2
    const top = window.screen.height / 2 - popupHeight / 2

    const popup = window.open(
      oauthUrl,
      'oauth2_login',
      `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
    )

    if (!popup) {
      throw new Error(
        'Popup blocked. Please allow popups for this site and try again.'
      )
    }

    popupRef.current = popup

    // Monitor popup close to handle manual close or missed postMessage events
    checkIntervalRef.current = setInterval(async () => {
      if (popup.closed) {
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current)
          checkIntervalRef.current = null
        }

        if (authResultHandledRef.current) {
          setLoading(false)
          return
        }

        let recoveredUser = null
        try {
          recoveredUser = await checkSession({ emitSyncEvent: true })
        } catch (sessionError) {
          console.warn('Session recovery check failed after popup close:', sessionError)
        }

        if (recoveredUser) {
          authResultHandledRef.current = true
          sessionStorage.removeItem('auth_flow_started')
          sessionStorage.removeItem('auth_provider')
          sessionStorage.removeItem('auth_start_time')
          sessionStorage.removeItem('oauth_original_domain')
          sessionStorage.removeItem('oauth_callback_domain')

          clarityService.trackEvent('oauth2_popup_closed_session_recovered', {
            provider: provider || 'unknown',
            timestamp: new Date().toISOString(),
          })

          setLoading(false)
          redirectAfterSignIn(navigate)
          return
        }

        setLoading(false)
        if (sessionStorage.getItem('auth_flow_started')) {
          const closedProvider =
            sessionStorage.getItem('auth_provider') || 'unknown'
          clarityService.trackEvent('oauth2_popup_closed_manually', {
            provider: closedProvider,
            timestamp: new Date().toISOString(),
          })
        }
      }
    }, 500)
  } catch (error) {
    console.error('Error initiating OAuth login:', error)
    setLoading(false)
    setError(`Failed to start authentication: ${error.message}`)

    clarityService.trackEvent('oauth2_login_error', {
      provider,
      error: error.message,
      domain: window.location.hostname,
      timestamp: new Date().toISOString(),
    })
  }
}

  return (
    <ThemeProvider theme={theme}>
      <LayoutContainer>
        {/* Hero Section */}
        <HeroSection>
          {/* Background gradient */}
          <BackgroundGradient />

          {/* Animated background shapes */}
          <AnimatedShape className='shape-1' />
          <AnimatedShape className='shape-2' />

          <Box
            sx={{
              textAlign: 'center',
              maxWidth: '600px',
              px: 2,
              position: 'relative',
              zIndex: 2,
            }}
          >
            <IconContainer>
              <Shield
                sx={{
                  fontSize: { xs: 28, sm: 32, md: 36 },
                  color: '#ffda1b',
                }}
              />
            </IconContainer>

            <Typography
              variant='h2'
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
                lineHeight: 1.2,
                mb: 2,
                animation: `${fadeInUp} 0.8s ease-out`,
                animationDelay: '0.2s',
                animationFillMode: 'both',
              }}
            >
              Welcome to GCC Tax Laws
            </Typography>

            <AccentLine />

            <Typography
              variant='h6'
              sx={{
                color: '#e2e8f0',
                fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.2rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                animation: `${fadeInUp} 0.8s ease-out`,
                animationDelay: '0.4s',
                animationFillMode: 'both',
              }}
            >
              Up-to-date GCC tax laws, organised for fast and precise research
            </Typography>
          </Box>
        </HeroSection>

        {/* Content Section */}
        <ContentSection maxWidth='lg' sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid
            container
            rowSpacing={{ xs: 4, md: 5 }}
            columnSpacing={{ xs: 4, md: 5, lg: 6, xl: 8 }}
            justifyContent='center'
            sx={{ width: '100%', maxWidth: 1180, mx: 'auto', alignItems: { xs: 'center', lg: 'stretch' } }}
          >
            {/* Authentication Card - Shows first on mobile */}
            <Grid
              item
              xs={12}
              lg={6}
              order={{ xs: 1, lg: 1 }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', lg: 'flex-end' },
                alignItems: { xs: 'center', lg: 'stretch' },
              }}
            >
              <AuthCard sx={{ mr: { lg: 'auto' }, ml: { lg: -6 } }}>
                <Box>
                  <Typography
                    variant='h4'
                    sx={{
                      textAlign: 'center',
                      mb: 3,
                      fontWeight: 600,
                      color: '#232536',
                      fontSize: { xs: '1.15rem', sm: '1.35rem' },
                      whiteSpace: 'nowrap',
                      animation: `${slideInLeft} 0.6s ease-out`,
                      animationDelay: '0.2s',
                      animationFillMode: 'both',
                    }}
                  >
                    Please login to use the portal
                  </Typography>

                  <Box
                    sx={{
                      mb: 3,
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      backgroundColor: '#f8fafc',
                      textAlign: 'center',
                      animation: `${fadeInUp} 0.6s ease-out`,
                      animationDelay: '0.3s',
                      animationFillMode: 'both',
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{ color: '#232536', fontWeight: 700, mb: 0.4 }}
                    >
                      One account. Everywhere.
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        color: '#4b5563',
                        fontSize: { xs: '0.82rem', sm: '0.88rem' },
                        lineHeight: 1.45,
                      }}
                    >
                      Create your account once and use the same
                      login across GTL ecosystem.
                      <Tooltip
                        arrow
                        placement='right'
                        disableInteractive={false}
                        title={
                          <Box sx={{ py: 0.4 }}>
                            <Box
                              component='div'
                              sx={{
                                fontSize: '0.75rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.3,
                              }}
                            >
                              <Link href='https://gcctaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>GCCTaxLaws.com</Link>
                              <Link href='https://uaetaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>UAETaxLaws.com</Link>
                              <Link href='https://ksataxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>KSATaxLaws.com</Link>
                              <Link href='https://kuwaittaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>KuwaitTaxLaws.com</Link>
                              <Link href='https://bahraintaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>BahrainTaxLaws.com</Link>
                              <Link href='https://omantaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>OmanTaxLaws.com</Link>
                              <Link href='https://qatartaxlaws.com' target='_blank' rel='noopener noreferrer' sx={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer', '&:hover': { color: 'inherit', textDecoration: 'underline' }, '&:visited': { color: 'inherit' } }}>QatarTaxLaws.com</Link>
                            </Box>
                          </Box>
                        }
                      >
                        <IconButton
                          size='small'
                          aria-label='View GTL ecosystem domains'
                          sx={{
                            ml: 0.3,
                            p: 0.25,
                            verticalAlign: 'middle',
                            color: '#232536',
                            '&:hover': { color: '#3a3e5a' },
                          }}
                        >
                          <InfoOutlined sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                  </Box>

                  {error && (
                    <Alert
                      severity='error'
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        animation: `${fadeInUp} 0.5s ease-out`,
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {sessionNotice && (
                    <Alert
                      severity='warning'
                      onClose={() => setSessionNotice('')}
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        animation: `${fadeInUp} 0.5s ease-out`,
                        bgcolor: 'rgba(255, 218, 27, 0.2)',
                        color: '#232536',
                        border: '1px solid rgba(35, 37, 54, 0.15)',
                        '& .MuiAlert-icon': {
                          color: '#232536',
                        },
                        '& .MuiAlert-action': {
                          color: '#232536',
                        },
                      }}
                    >
                      {sessionNotice}
                    </Alert>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2.5,
                      mb: 3,
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <SocialButton
                        fullWidth
                        startIcon={<LinkedIn />}
                        onClick={() => handleOAuth2Login('linkedin')}
                        disabled={loading}
                        sx={{
                          '& .MuiButton-startIcon svg': {
                            color: '#0A66C2',
                          },
                          animation: `${slideInLeft} 0.6s ease-out`,
                          animationDelay: '0.4s',
                          animationFillMode: 'both',
                        }}
                      >
                        Continue with LinkedIn
                      </SocialButton>
                      {/* <Chip
                        label='Recommended'
                        size='small'
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: '#ffda1b',
                          color: '#232536',
                          fontWeight: 700,
                          fontSize: '0.65rem',
                          height: '20px',
                          boxShadow: '0 2px 8px rgba(255, 218, 27, 0.4)',
                          animation: `${pulse} 2s ease-in-out infinite`,
                          '& .MuiChip-label': {
                            padding: '0 8px',
                          },
                        }}
                      /> */}
                    </Box>
                    {/* <SocialButton
                      fullWidth
                      startIcon={<GoogleLogo />}
                      onClick={() => handleOAuth2Login('google')}
                      disabled={loading}
                      sx={{
                        animation: `${slideInLeft} 0.6s ease-out`,
                        animationDelay: '0.6s',
                        animationFillMode: 'both',
                      }}
                    >
                      Continue with Google
                    </SocialButton> */}
                  </Box>

                  {loading && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 3,
                        animation: `${fadeInUp} 0.3s ease-out`,
                      }}
                    >
                      <CircularProgress
                        size={24}
                        sx={{
                          color: '#ffda1b',
                          animation: `${pulse} 1.5s ease-in-out infinite`,
                        }}
                      />
                    </Box>
                  )}

                  <Divider
                    sx={{
                      mb: 3,
                      animation: `${fadeInUp} 0.6s ease-out`,
                      animationDelay: '0.8s',
                      animationFillMode: 'both',
                    }}
                  />

                  <Typography
                    variant='body2'
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.8rem', sm: '0.85rem' },
                      lineHeight: 1.5,
                      textAlign: 'center',
                      animation: `${fadeInUp} 0.6s ease-out`,
                      animationDelay: '1s',
                      animationFillMode: 'both',
                    }}
                  >
                    <Box
                      component='span'
                      sx={{ color: 'red', fontWeight: 'bold' }}
                    >
                      *{' '}
                    </Box>
                    By continuing, you agree to our{' '}
                    <Link
                      component={RouterLink}
                      to='/terms-and-conditions'
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        color: '#232536',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        textDecorationColor: '#ffda1b',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '3px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#ffda1b',
                          textDecorationThickness: '2px',
                        },
                      }}
                    >
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link
                      component={RouterLink}
                      to='/privacy-policy'
                      target='_blank'
                      rel='noopener noreferrer'
                      sx={{
                        color: '#232536',
                        fontWeight: 600,
                        textDecoration: 'underline',
                        textDecorationColor: '#ffda1b',
                        textDecorationThickness: '1px',
                        textUnderlineOffset: '3px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          color: '#ffda1b',
                          textDecorationThickness: '2px',
                        },
                      }}
                    >
                      Privacy Policy
                    </Link>
                  </Typography>
                </Box>
              </AuthCard>
            </Grid>
            {/* Video Section - Shows second on mobile */}
            <Grid
              item
              xs={12}
              lg={6}
              order={{ xs: 2, lg: 2 }}
              sx={{
                display: 'flex',
                justifyContent: { xs: 'center', lg: 'flex-start' },
                alignItems: 'center',
                ml: { lg: -10 },
              }}
            >
              <VideoComponent />
            </Grid>
          </Grid>
        </ContentSection>
      </LayoutContainer>
    </ThemeProvider>
  )
}

export default SignIn
